/**
 * Credits API Endpoints
 *
 * Client functions for credit-related API calls.
 * These functions are used by React Query hooks.
 */

import { supabase } from "@/integrations/supabase/client";
import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type {
  CheckActionCreditsRequest,
  CheckActionCreditsResponse,
  CheckCreditsResponse,
  DeductCreditsRequest,
  DeductCreditsResponse,
  GetCreditHistoryResponse,
  GetCreditsBalanceResponse,
} from "../types/credits";

/**
 * Get the current user's credit balance
 *
 * @returns Promise with credit balance
 * @throws {Error} If the response format is invalid
 * @throws {InsufficientCreditsError} Never for this endpoint
 *
 * @example
 * const balance = await getCreditsBalance();
 * console.log(balance.credits); // 500
 */
export async function getCreditsBalance(): Promise<GetCreditsBalanceResponse> {
  const { data, error } = await supabase.functions.invoke(
    "api/credits/balance",
    {
      method: "GET",
    }
  );

  // Handle errors (network issues, 500s, etc.)
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || typeof data.credits !== "number") {
    console.error("Invalid credits balance response:", data);
    throw new Error("Invalid response format from credits/balance endpoint");
  }

  return data;
}

/**
 * Deduct credits from the current user's balance
 *
 * @param request - Action type, optional session_id, and optional metadata
 * @returns Promise with success status and new balance
 * @throws {InsufficientCreditsError} If user doesn't have enough credits
 * @throws {Error} For other errors
 *
 * @example
 * try {
 *   const result = await deductCredits({
 *     action_type: "pdf_download",
 *     session_id: "123",
 *     metadata: { report_id: "456" }
 *   });
 *   console.log(result.new_balance); // 350
 * } catch (error) {
 *   if (error instanceof InsufficientCreditsError) {
 *     console.log(`Need ${error.shortage} more credits`);
 *   }
 * }
 */
export async function deductCredits(
  request: DeductCreditsRequest
): Promise<DeductCreditsResponse> {
  const { data, error } = await supabase.functions.invoke(
    "api/credits/deduct",
    {
      method: "POST",
      body: request,
    }
  );

  // Handle errors (including insufficient credits)
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || typeof data.success !== "boolean") {
    console.error("Invalid deduct credits response:", data);
    throw new Error("Invalid response format from credits/deduct endpoint");
  }

  return data;
}

/**
 * Get credit transaction history for the current user
 *
 * @param limit - Number of transactions to fetch (default: 10)
 * @param offset - Offset for pagination (default: 0)
 * @returns Promise with transaction history
 * @throws {Error} If the response format is invalid
 *
 * @example
 * const history = await getCreditHistory(20, 0);
 * console.log(history.transactions.length); // 20
 * console.log(history.total); // 150
 */
export async function getCreditHistory(
  limit = 10,
  offset = 0
): Promise<GetCreditHistoryResponse> {
  const { data, error } = await supabase.functions.invoke(
    `api/credits/history?limit=${limit}&offset=${offset}`,
    {
      method: "GET",
    }
  );

  // Handle errors
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || !Array.isArray(data.transactions)) {
    console.error("Invalid credit history response:", data);
    throw new Error("Invalid response format from credits/history endpoint");
  }

  return {
    transactions: data.transactions,
    total: data.total ?? 0,
    limit: data.limit ?? limit,
    offset: data.offset ?? offset,
  };
}

/**
 * Check if the current user has enough credits for an action
 *
 * @param amount - Amount of credits to check
 * @returns Promise with check result
 * @throws {Error} If the response format is invalid
 *
 * @example
 * const check = await checkCredits(150);
 * if (check.has_enough) {
 *   console.log("User can proceed");
 * } else {
 *   console.log(`Need ${check.required - check.current} more credits`);
 * }
 */
export async function checkCredits(
  amount: number
): Promise<CheckCreditsResponse> {
  const { data, error } = await supabase.functions.invoke(
    `api/credits/check?amount=${amount}`,
    {
      method: "GET",
    }
  );

  // Handle errors
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || typeof data.has_enough !== "boolean") {
    console.error("Invalid check credits response:", data);
    throw new Error("Invalid response format from credits/check endpoint");
  }

  return data;
}

/**
 * Check if the user has enough credits for a specific action
 * Also checks if the action was already purchased for the session
 *
 * @param request - Action type and optional session ID
 * @returns Promise with check result including purchase status
 * @throws {Error} If the response format is invalid
 *
 * @example
 * const check = await checkActionCredits({
 *   session_id: "abc-123",
 *   action_type: "nis2_service_analysis"
 * });
 *
 * if (check.already_purchased) {
 *   console.log("Action already purchased - proceed without charge");
 * } else if (check.has_credits) {
 *   console.log("User has enough credits");
 * } else {
 *   console.log(`Need ${check.shortage} more credits`);
 * }
 */
export async function checkActionCredits(
  request: CheckActionCreditsRequest
): Promise<CheckActionCreditsResponse> {
  const params = new URLSearchParams();
  params.append("actionType", request.action_type);
  params.append("sessionId", request.session_id);

  const { data, error } = await supabase.functions.invoke(
    `api/credits/check-action?${params.toString()}`,
    {
      method: "GET",
    }
  );

  // Handle errors
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || typeof data.has_credits !== "boolean") {
    console.error("Invalid check action credits response:", data);
    throw new Error(
      "Invalid response format from credits/check-action endpoint"
    );
  }

  return data;
}
